import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSettings } from "@/contexts/SettingsContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, FileSignature, Eraser } from "lucide-react";

function SignaturePad({ onChange, dataTestId = "signature-pad" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  // Set up canvas pixel ratio
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0F172A";
  }, []);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const url = canvasRef.current.toDataURL("image/png");
    onChange?.(url);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange?.("");
  };

  return (
    <div className="space-y-2">
      <div className="relative border border-border bg-white">
        <canvas
          ref={canvasRef}
          data-testid={dataTestId}
          className="block w-full h-48 touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
        <div className="absolute bottom-2 right-2 text-[10px] uppercase tracking-widest text-muted-foreground pointer-events-none">
          sign here
        </div>
      </div>
      <button
        type="button"
        onClick={clear}
        data-testid="signature-clear"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
      >
        <Eraser className="w-3.5 h-3.5" />
        Clear signature
      </button>
    </div>
  );
}

function FileField({ id, label, hint, onChange, file, testId }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="font-medium">{label}</Label>
      <div className="border border-dashed border-border bg-white p-4 flex items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground truncate">
          {file ? <span className="text-foreground">{file.name}</span> : hint}
        </div>
        <label className="text-sm font-medium underline underline-offset-4 cursor-pointer whitespace-nowrap" htmlFor={id}>
          {file ? "Change" : "Choose file"}
        </label>
        <input
          id={id}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          data-testid={testId}
          onChange={(e) => onChange(e.target.files?.[0] || null)}
        />
      </div>
    </div>
  );
}

export default function PatientConsent() {
  const { settings: s } = useSettings();
  const title = s.consent_title || "Patient Informed Consent & Service Agreement";
  const body = s.consent_body || "";

  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [idFile, setIdFile] = useState(null);
  const [rxFile, setRxFile] = useState(null);
  const [signature, setSignature] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please complete name, email and phone.");
      return;
    }
    if (!idFile || !rxFile) {
      toast.error("Please upload both your ID and your medical prescription.");
      return;
    }
    if (!signature) {
      toast.error("Please sign in the box before submitting.");
      return;
    }
    if (!agree) {
      toast.error("Please confirm that you accept the contract.");
      return;
    }

    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("email", form.email.trim());
    fd.append("phone", form.phone.trim());
    fd.append("signature", signature);
    fd.append("id_file", idFile);
    fd.append("prescription_file", rxFile);

    setSubmitting(true);
    try {
      await api.post("/patient-consent", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      toast.success("Consent submitted. Thank you!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="consent-done" className="max-w-2xl mx-auto px-6 py-24 text-center">
        <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
        <h1 className="font-display text-4xl font-bold mt-6">Consent received</h1>
        <p className="mt-4 text-muted-foreground">
          Thank you, {form.name}. A copy of your signed contract has been sent to the medical team.
          You may close this window now.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="patient-consent-page" className="max-w-3xl mx-auto px-6 lg:px-8 py-12 lg:py-20">
      <div className="overline mb-3">Patient consent form</div>
      <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">{title}</h1>
      <p className="mt-4 text-sm text-muted-foreground">
        Please read the contract carefully, fill in your details, upload your ID and medical
        prescription, and sign at the bottom.
      </p>

      {/* Contract body */}
      <div className="mt-10 border border-border bg-white p-6 lg:p-8">
        <div className="overline mb-4">Agreement</div>
        <div className="prose prose-sm max-w-none text-sm leading-relaxed text-foreground whitespace-pre-wrap" data-testid="consent-body">
          {body}
        </div>
      </div>

      <form onSubmit={submit} className="mt-10 space-y-8" data-testid="consent-form">
        {/* Patient details */}
        <section className="border border-border bg-white p-6 lg:p-8 space-y-5">
          <h2 className="font-display text-2xl font-semibold">Your details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                data-testid="consent-name"
                value={form.name}
                onChange={onChange("name")}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="consent-email"
                value={form.email}
                onChange={onChange("email")}
                required
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                data-testid="consent-phone"
                value={form.phone}
                onChange={onChange("phone")}
                placeholder="+1 555 555 0000"
                required
              />
            </div>
          </div>
        </section>

        {/* Uploads */}
        <section className="border border-border bg-white p-6 lg:p-8 space-y-5">
          <h2 className="font-display text-2xl font-semibold">Documents</h2>
          <FileField
            id="id_file"
            testId="consent-id-file"
            label="Photo of your ID"
            hint="JPG, PNG, WEBP or PDF. Max 10MB."
            file={idFile}
            onChange={setIdFile}
          />
          <FileField
            id="rx_file"
            testId="consent-rx-file"
            label="Medical prescription"
            hint="JPG, PNG, WEBP or PDF. Max 10MB."
            file={rxFile}
            onChange={setRxFile}
          />
        </section>

        {/* Signature */}
        <section className="border border-border bg-white p-6 lg:p-8 space-y-4">
          <div className="flex items-center gap-2">
            <FileSignature className="w-5 h-5" />
            <h2 className="font-display text-2xl font-semibold">Electronic signature</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Draw your signature in the box below using your finger, stylus or mouse.
          </p>
          <SignaturePad onChange={setSignature} />
        </section>

        {/* Agreement checkbox */}
        <label className="flex items-start gap-3 text-sm leading-relaxed cursor-pointer" data-testid="consent-agree">
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            className="mt-1 w-4 h-4"
          />
          <span>
            I have read and understood the contract above, the information provided is accurate, and I accept
            the terms.
          </span>
        </label>

        <Button
          type="submit"
          size="lg"
          disabled={submitting}
          className="w-full sm:w-auto rounded-md h-12 px-8"
          data-testid="consent-submit"
        >
          {submitting ? "Sending…" : "Sign and submit"}
        </Button>

        <p className="text-xs text-muted-foreground">
          By submitting, a PDF copy of this contract with your signature, along with the uploaded ID and
          prescription, will be sent to the medical team.
        </p>
      </form>
    </div>
  );
}
